import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import type { ComponentProps, MouseEventHandler, ReactElement, ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileRowInclusionItem from './SearchTileRowInclusionItem'
import LabeledSheet from 'LuxKit/LabeledSheet'
import { partitionBy, take } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { trackClientEvent } from 'analytics/analytics'

const HighlightedInclusionsContainer = styled(Group)`
  padding: ${rem(8)};
`

type InheritedSearchTileRowInclusionItemProps = Pick<ComponentProps<typeof SearchTileRowInclusionItem>, 'lineClamp' | 'isHighlighted' | 'variant'>

interface Props extends InheritedSearchTileRowInclusionItemProps {
  inclusionsTitle?: string | ReactElement
  inclusions: Array<App.OfferInclusion>
  /**
   * The number of items to display before the rest are truncated.
   *
   * If set to zero, nothing will be displayed.
   */
  truncateAfter?: number
  onShowMoreClick: () => void
  showSelectPackageOnlyLabel?: boolean
  /**
   * Extra hidden items rendered outside this list (e.g. loyalty inclusions)
   * that should be counted in the "+N more" link total.
   */
  additionalHiddenCount?: number
  /**
   * When set, replaces the "+N more" copy. Shown even if this list is not truncated.
   */
  overflowLabel?: string
  /**
   * Rendered as the first row in this list (same gap, icon column and type scale).
   */
  leading?: ReactNode
}

function SearchTileStackInclusionsList({
  lineClamp,
  isHighlighted,
  variant,
  inclusionsTitle,
  inclusions,
  truncateAfter,
  onShowMoreClick,
  showSelectPackageOnlyLabel,
  additionalHiddenCount = 0,
  overflowLabel,
  leading,
}: Props) {
  const intl = useIntl()
  const [flashInclusionItems, nonFlashInclusionItems] = useMemo(() => partitionBy(inclusions, inclusion => !!inclusion.isFlash), [inclusions])
  const [bonusInclusionItems, inclusionItems] = useMemo(() => partitionBy(nonFlashInclusionItems, inclusion => !!inclusion.isLuxPlusBonus), [nonFlashInclusionItems])
  const regularInclusionsTruncateLength = !truncateAfter || (flashInclusionItems.length + bonusInclusionItems.length) >= (truncateAfter ?? 0) ? 0 : truncateAfter - (flashInclusionItems.length + bonusInclusionItems.length)
  const flashInclusionsTruncateLength = !bonusInclusionItems.length || !truncateAfter ? (truncateAfter ?? 0) : Math.max((truncateAfter - bonusInclusionItems.length), 0)
  const [alwaysVisibleInclusions, collapsibleInclusions] = useListTruncator(regularInclusionsTruncateLength, inclusionItems)
  const [alwaysVisibleFlashInclusions, collapsibleFlashInclusions] = useListTruncator(flashInclusionsTruncateLength, flashInclusionItems)
  const handleMoreClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()

    trackClientEvent({
      subject: 'read_more_inclusions',
      action: 'click',
      category: 'search_page',
      type: 'interaction',
    })

    onShowMoreClick()
  }, [onShowMoreClick])

  if (!leading && !overflowLabel && !alwaysVisibleInclusions.length && !flashInclusionItems.length && !bonusInclusionItems.length) return null

  return <Group direction="vertical" gap={4}>
    {(!!inclusionsTitle || showSelectPackageOnlyLabel) && <BodyText variant="medium" weight="bold">
      {inclusionsTitle} {showSelectPackageOnlyLabel && <StructureLabel variant="promotion">{intl.formatMessage({
        id: 'lEOwpwEFOxorF9SnVSKV',
        defaultMessage: 'Specific packages only',
        description: 'Label indicating inclusions apply to specific packages only on search tile',
      })}
      </StructureLabel>}
    </BodyText>}
    {leading}
    {!!bonusInclusionItems.length && truncateAfter && <LabeledSheet kind="lux-plus" label={<LuxPlusLabel type="bonus-inclusions" />}>
      <HighlightedInclusionsContainer direction="vertical" gap={4}>
        {take(bonusInclusionItems, truncateAfter).map((inclusion) => <SearchTileRowInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          lineClamp={lineClamp}
          variant={variant}
        />)}
      </HighlightedInclusionsContainer>
    </LabeledSheet>}
    {!!flashInclusionItems.length && !bonusInclusionItems.length && truncateAfter && <LabeledSheet
      kind="exclusive"
      label={intl.formatMessage({
        id: 'S9lPa89icfVyTrNUpLpr',
        defaultMessage: 'Limited time inclusions',
        description: 'Label for flash/limited time inclusions section on search tile',
      })}
    >
      <HighlightedInclusionsContainer direction="vertical" gap={4}>
        {take(flashInclusionItems, truncateAfter).map((inclusion) => <SearchTileRowInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          lineClamp={lineClamp}
          variant={variant}
        />)}
      </HighlightedInclusionsContainer>
    </LabeledSheet>}
    {!!flashInclusionItems.length && !!bonusInclusionItems.length &&
      alwaysVisibleFlashInclusions.map((inclusion) => <SearchTileRowInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        isHighlighted={false}
        lineClamp={lineClamp}
        variant={variant}
      />)}
    {alwaysVisibleInclusions.map((inclusion) => <SearchTileRowInclusionItem
      key={inclusion.id}
      inclusion={inclusion}
      isHighlighted={isHighlighted}
      lineClamp={lineClamp}
      variant={variant}
    />)}
    {!!overflowLabel && <TextLink size={variant === 'small' ? 'small' : 'medium'} onClick={handleMoreClick}>{overflowLabel}</TextLink>}
    {!overflowLabel && (!!collapsibleInclusions.length || !!additionalHiddenCount) && <TextLink size="medium" onClick={handleMoreClick}>{intl.formatMessage({
      id: 'AIwnWOOt3xcnn1pa1RAK',
      defaultMessage: '+{count} more',
      description: 'Link to show more truncated inclusions on search tile',
    }, { count: collapsibleInclusions.length + collapsibleFlashInclusions.length + additionalHiddenCount })}
    </TextLink>}
  </Group>
}

export default SearchTileStackInclusionsList
